/**
 * Types shared between the browser apps and the Cloud Functions.
 *
 * Both sides import from here so a field rename shows up as a compile error
 * rather than as a reservation that quietly loses its phone number.
 *
 * Timestamps are milliseconds since the epoch rather than Firestore
 * `Timestamp` objects, because that type differs between the web SDK and the
 * Admin SDK. Functions convert at the boundary.
 */

export type Millis = number;

/** Money is always integer cents. Never a float, never a string. */
export type Cents = number;

// ---------------------------------------------------------------------------
// Events — one document per swap meet
// ---------------------------------------------------------------------------

export interface SwapMeetEvent {
  id: string;
  name: string;
  /** Shown to vendors, e.g. "Saturday, November 21, 2026 · 9:00am–1:00pm". */
  dateLabel: string;
  /** Vendor setup time, which differs from doors opening. */
  setupLabel?: string;
  startsAt: Millis;
  endsAt?: Millis;
  setupFromAt?: Millis;
  venueName: string;
  venueAddress: string;
  /** Where a vendor's questions go. Printed on confirmations. */
  contactEmail?: string;
  /** Public admission, shown for context; unrelated to table fees. */
  admissionLabel?: string;

  /**
   * What a table costs when it is reserved and paid for ahead of the meet.
   * A table may override it via `priceOverride`.
   */
  pricePerTable: Cents;
  /**
   * What a table costs on the day. Higher on purpose: the gap is the club's
   * incentive to reserve early, and it is a far better lever than a surcharge
   * because it rewards the behaviour rather than punishing the alternative.
   */
  dayOfPricePerTable: Cents;
  /** Most tables a single vendor may take in one reservation. */
  maxTablesPerVendor: number;

  /** Vendors may only reserve between these two moments. */
  reservationsOpenAt: Millis;
  reservationsCloseAt: Millis;

  /** Drafts are admin-only, so a layout can be built before going live. */
  published: boolean;

  /** Deadlines live on the event so the club can tighten them without a deploy. */
  policy: EventPolicy;

  /** The SVG coordinate space the table rectangles are laid out in. */
  mapWidth: number;
  mapHeight: number;

  createdAt: Millis;
  updatedAt: Millis;
}

export interface EventPolicy {
  /** How long a vendor's cart locks tables while they fill in the form. */
  holdMinutes: number;
  /** How long tables stay locked once a PayPal order is open. */
  paypalHoldMinutes: number;
  /**
   * Whether the check deadline is satisfied by the envelope ARRIVING, or by
   * the funds clearing the bank.
   *
   * The club's rule is arrival: a check received by the advance deadline keeps
   * the tables, and clearing afterwards simply confirms them. This matters to
   * the expiry sweep — it must release reservations still sitting in
   * `awaiting_check` at the deadline, and leave `check_received` ones alone,
   * because those vendors have done everything asked of them.
   */
  checkDeadlineIsReceipt: boolean;
  /**
   * Tentative reservations are also released this many days before the meet,
   * whichever comes first — a check that clears the day before is no use when
   * the hall has to be laid out.
   */
  tentativeCutoffDaysBeforeEvent: number;
  /** Whether vendors may ask to pay at the door at all. */
  allowPayAtDoor: boolean;
  /**
   * Which rate an approved pay-at-the-meet reservation is charged.
   *
   * `true`  — they pay the day-of rate, because the money arrives on the day.
   * `false` — they pay the advance rate, because they booked ahead.
   *
   * This is a policy call for the club, not a technical one, which is why it
   * is a field rather than a constant.
   */
  atDoorUsesDayOfRate: boolean;
  /** Whether vendors may mail a check at all. */
  allowCheckByMail: boolean;
  /** Shown verbatim at checkout. Settle this with the board before launch. */
  refundPolicyText: string;
  /**
   * Where to post a check. Shown the moment a vendor picks that option, not
   * only afterwards in an email — somebody deciding how to pay needs to know
   * where the envelope goes before they commit to it.
   */
  checkMailingAddress: string;
  /** Exactly who the check is made out to. Wrong payee means a bounced check. */
  checkPayableTo: string;
}

// ---------------------------------------------------------------------------
// Tables — the authoritative inventory, one document per physical table
// ---------------------------------------------------------------------------

/**
 * A table's state, from the inventory's point of view.
 *
 * `held` covers every tentative situation — a live cart, an open PayPal order,
 * a promised check, an unapproved request. The vendor-facing map only needs to
 * know "not available"; the reservation carries the detail of why.
 */
export type TableStatus =
  | 'available'
  | 'held'
  | 'sold'
  /** Withheld by the club: damaged, behind a pillar, reserved for the raffle. */
  | 'blocked';

export interface Table {
  id: string;
  /** What the vendor and the club call it, e.g. "A12". */
  label: string;
  /** Grouping for pricing and for the printed roster, e.g. "Main hall". */
  section: string;

  /** Position in the event's map coordinate space. */
  x: number;
  y: number;
  w: number;
  h: number;

  /** Overrides the event price — corner and door tables often cost more. */
  priceOverride: Cents | null;

  /**
   * Within reach of one of the hall's electrical outlets. Read off the floor
   * plan rather than entered by hand — vendors charging packs or running
   * lighting care about this, so it is worth surfacing on the map.
   */
  nearPower: boolean;

  status: TableStatus;
  /** Set while `status === 'held'`; the sweep releases the table after this. */
  holdExpiresAt: Millis | null;
  /** Which reservation holds or owns this table. */
  reservationId: string | null;

  updatedAt: Millis;
}

/**
 * What the vendor app is allowed to see: enough to draw and price the map,
 * with no vendor identity attached. Mirrored out of `tables` by a function.
 */
export interface PublicTable {
  id: string;
  label: string;
  section: string;
  x: number;
  y: number;
  w: number;
  h: number;
  price: Cents;
  nearPower: boolean;
  /** `held` deliberately does not say whose, or why. */
  status: TableStatus;
}

/** Everything the vendor app needs to draw the hall, in one public document. */
export interface PublicMapChunk {
  eventId: string;
  tables: PublicTable[];
  /** Coordinate space the table rectangles live in. */
  map: { width: number; height: number };
  /**
   * Where the drawing actually sits inside that space.
   *
   * The club's CorelDRAW export authored a viewBox of `0 0 148000 91000`
   * while placing the plan at roughly x 6112..149439, so rendering against
   * the raw viewBox leaves a blank band down the left. Fit to this instead.
   */
  content?: { x: number; y: number; w: number; h: number };
  /** The building's architectural plan, placed in that same space. */
  background: { href: string; x: number; y: number; w: number; h: number };
  outlets: { x: number; y: number; w: number; h: number }[];
  checkInTables: { x: number; y: number; w: number; h: number }[];
  updatedAt: Millis;
}

// ---------------------------------------------------------------------------
// Reservations
// ---------------------------------------------------------------------------

export type PaymentMethod =
  | 'paypal'
  | 'check'
  | 'at_door';

/**
 * The reservation lifecycle. See the architecture plan for the diagram.
 *
 *   hold ─┬─ paypal  → awaiting_capture ──────────────────→ confirmed
 *         ├─ check   → awaiting_check → check_cleared ────→ confirmed
 *         └─ at_door → pending_approval ───────────────────→ confirmed
 *         └─ timeout → expired
 *
 * Only `confirmed` means the table is genuinely the vendor's. Everything
 * between `hold` and `confirmed` shows on the map as unavailable but unsold,
 * and every one of those states carries a deadline.
 */
export type ReservationStatus =
  /** Tables locked while the vendor fills in the form. */
  | 'hold'
  /** A PayPal order is open and unpaid. */
  | 'awaiting_capture'
  /** Vendor says a check is in the mail. Tentative. */
  | 'awaiting_check'
  /** Check arrived but has not cleared the bank yet. Still tentative. */
  | 'check_received'
  /** Funds are in the bank. A function promotes this to `confirmed`. */
  | 'check_cleared'
  /** Vendor asked to pay at the meet; an admin has to agree. Tentative. */
  | 'pending_approval'
  /** The table is theirs. */
  | 'confirmed'
  /** Vendor or admin backed out; tables released. */
  | 'cancelled'
  /** A deadline passed unmet; tables released. */
  | 'expired'
  /** Paid, then refunded. Kept for the treasurer rather than deleted. */
  | 'refunded';

/** States where the vendor holds the tables but has not paid for them. */
export const TENTATIVE_STATUSES: readonly ReservationStatus[] = [
  'hold',
  'awaiting_capture',
  'awaiting_check',
  'check_received',
  'pending_approval',
];

/** States where the tables should be back on the map. */
export const RELEASED_STATUSES: readonly ReservationStatus[] = [
  'cancelled',
  'expired',
  'refunded',
];

export function isTentative(status: ReservationStatus): boolean {
  return TENTATIVE_STATUSES.includes(status);
}

export function holdsTables(status: ReservationStatus): boolean {
  return !RELEASED_STATUSES.includes(status);
}

export interface Vendor {
  name: string;
  /** Business or club name shown on the roster, if they trade under one. */
  businessName: string | null;
  email: string;
  phone: string;
  /**
   * Postal address. Wanted for the club's own records, and it is the return
   * address the treasurer already has in hand when a mailed check needs
   * chasing or refunding.
   */
  address: string | null;
  /** Academy of Model Aeronautics membership number. */
  amaNumber: string | null;
  /** What they plan to sell — useful for the club's own advertising. */
  sellingDescription: string | null;
}

export interface Reservation {
  id: string;
  /**
   * The short code a human actually uses: "LMAC-7K4Q".
   *
   * The Firestore document id is twenty random characters — fine for software,
   * useless written on a check memo line or read down the phone. Every way a
   * payment reaches this club outside the digital path arrives attached to
   * something a person typed, so there has to be an identifier a person can
   * reliably type.
   *
   * Generated server-side and unique within the event. See `formatCode`.
   */
  code: string;
  eventId: string;
  vendor: Vendor;

  /** Tables claimed, by table document ID. */
  tableIds: string[];
  /** Labels copied in at claim time, so a receipt survives a map edit. */
  tableLabels: string[];

  amountDue: Cents;
  amountPaid: Cents;
  paymentMethod: PaymentMethod | null;

  status: ReservationStatus;
  /** When the current tentative state lapses. Null once resolved. */
  expiresAt: Millis | null;

  /** Anonymous-auth UID of the browser that created the hold. */
  sessionUid: string;
  /**
   * Long random token emailed to the vendor so they can view or cancel their
   * reservation without an account. Never exposed on the public map.
   */
  lookupToken: string;

  /** Set by functions once PayPal is involved. */
  paypalOrderId: string | null;
  paypalCaptureId: string | null;

  /** Free text for the club: "check #1042, deposited 2/19". */
  adminNotes: string | null;
  /** UID of the admin who approved a pay-at-door or cleared a check. */
  decidedByUid: string | null;
  decidedAt: Millis | null;

  /**
   * Marked on the day of the meet, from the door roster.
   *
   * Deliberately NOT written into `adminNotes`. Checking forty vendors in
   * would put forty lines of audit history onto the one sheet a volunteer is
   * reading at the door, which is the opposite of what that sheet is for.
   * Arrival is a fact about today, not a decision anyone needs to reconstruct
   * in March.
   */
  checkedInAt: Millis | null;
  /**
   * Which officer ticked them in. Optional because every reservation written
   * before check-in existed simply has no such field — read it with `?? null`
   * rather than assuming Firestore returns one.
   */
  checkedInBy?: string | null;

  createdAt: Millis;
  updatedAt: Millis;
}

// ---------------------------------------------------------------------------
// Payments — written only from the PayPal webhook and capture call
// ---------------------------------------------------------------------------

export interface PaymentRecord {
  id: string;
  provider: 'paypal';
  reservationId: string;
  eventId: string;
  providerOrderId: string;
  providerCaptureId: string | null;
  amount: Cents;
  currency: 'USD';
  status: 'created' | 'captured' | 'failed' | 'refunded';
  /** Raw provider payload, kept verbatim for reconciliation and disputes. */
  raw: unknown;
  createdAt: Millis;
  updatedAt: Millis;
}

// ---------------------------------------------------------------------------
// Callable function contracts
// ---------------------------------------------------------------------------

/** Claim tables atomically. Fails whole if any requested table is gone. */
export interface ClaimTablesRequest {
  eventId: string;
  tableIds: string[];
}

export interface ClaimTablesResponse {
  reservationId: string;
  /** Server-computed. The browser never sends or trusts a price. */
  amountDue: Cents;
  expiresAt: Millis;
}

/** Attach vendor details and a payment method to an existing hold. */
export interface SubmitReservationRequest {
  reservationId: string;
  vendor: Vendor;
  paymentMethod: PaymentMethod;
}

export interface SubmitReservationResponse {
  status: ReservationStatus;
  /** Present only for `paypal`; hand this to the PayPal JS SDK. */
  paypalOrderId: string | null;
  expiresAt: Millis | null;
}

export interface ReleaseHoldRequest {
  reservationId: string;
}

/**
 * What an officer can do to a reservation. Every one is audited onto the
 * record with who did it and when, because the treasurer reconciling in March
 * needs to know why table 44 shows paid with no PayPal receipt behind it.
 */
export type AdminDecision =
  /** Pay-at-the-meet request accepted. Tables held to the day. */
  | { kind: 'approve_at_door'; note?: string }
  /** Pay-at-the-meet request refused. Tables released. */
  | { kind: 'decline'; note?: string }
  /**
   * The envelope arrived. This is the one that beats the deadline: the club's
   * rule is receipt by 15 November, not clearance, so this stops the sweep
   * from releasing the tables even though the money has not landed yet.
   *
   * `amount` is what the check was actually written for, which is the moment
   * the club learns it — the officer is holding the check. It is not assumed
   * to be the amount owed: people round, misread the rate, or pay for a table
   * they later added, and a reservation that quietly records the owed amount
   * instead of the received one hides every one of those.
   */
  | { kind: 'mark_check_received'; amount?: Cents; note?: string }
  /**
   * Funds cleared the bank. Confirms the reservation.
   *
   * Carries no amount of its own in the normal case: clearing confirms the
   * sum recorded on arrival rather than replacing it.
   */
  | { kind: 'mark_check_cleared'; amount?: Cents; note?: string }
  /** Cash, card at a meeting, or anything else settled outside the app. */
  | { kind: 'record_payment'; amount: Cents; method: string; note?: string }
  /**
   * Hold the tables past the advance deadline.
   *
   * A club runs on judgement as much as rules: a long-standing member whose
   * check is genuinely in the post, someone who rang to explain. This keeps
   * the tables to the day of the meet instead of letting the sweep take them,
   * and records who decided so.
   */
  | { kind: 'waive_deadline'; note?: string }
  | { kind: 'cancel'; note?: string };

/** An officer reserving tables for somebody who paid outside the app. */
export interface AdminCreateReservationRequest {
  tableIds: string[];
  vendor: Vendor;
  /**
   * How they paid, if they have. Omit for a reservation the club is holding
   * on somebody's word — it then behaves exactly like a vendor's own
   * pay-at-the-meet request and needs the same approval.
   */
  payment?: { amount: Cents; method: string; note?: string };
  /**
   * Charge the day-of rate instead of the advance rate. Defaults to the
   * advance rate: the club's rule is that a manually written reservation is
   * $15 right up to the meet, since an officer taking money in October has
   * been paid in advance whatever the calendar says.
   */
  useDayOfRate?: boolean;
}

export interface AdminDecideRequest {
  reservationId: string;
  decision: AdminDecision;
}

/**
 * Correcting who the vendor is, after the fact.
 *
 * Deliberately narrower than `AdminDecision`: this changes the person, never
 * the booking. The confirmation code, the tables and both money columns are
 * absent from this request and rejected by the function, for three separate
 * reasons.
 *
 * The code is what the vendor wrote down and what the treasurer reads off a
 * check memo line; changing it invalidates paper that is already in the post.
 * Tables move only through the atomic claim transaction, so a vendor who wants
 * different ones is cancelled and rebooked by an officer rather than edited in
 * place. And the amounts are the audited product of PayPal captures and
 * officer decisions, which is precisely why they are trustworthy.
 *
 * What is left is the part that gets fat-fingered at reservation time and that
 * the club actually runs on: the name the door roster sorts by, and the three
 * ways of reaching somebody.
 */
export interface AdminUpdateVendorRequest {
  reservationId: string;
  vendor: Vendor;
  /** Why the correction was made, kept on the record beside it. */
  note?: string;
}

/** A line of free text onto a reservation's history. Appends; never replaces. */
export interface AdminAddNoteRequest {
  reservationId: string;
  note: string;
}

/**
 * Send the vendor their letter again.
 *
 * Not a status change and not a new kind of letter — it re-queues exactly the
 * message the reservation's current state calls for. See `letterFor` in the
 * functions package, which both this and the automatic mail go through, so the
 * resent copy can never drift from the original.
 */
export interface AdminResendLetterRequest {
  reservationId: string;
}

/**
 * Statuses a vendor has already been written to about, and so the only ones
 * worth writing to again.
 *
 * A `hold` or an open card payment never generated a letter, so there is
 * nothing to resend. The terminal states did generate one, but resending a
 * cancellation or an expiry to somebody who has moved on is a message nobody
 * asked for a second time — an officer who genuinely needs to re-explain one
 * of those can write an email.
 */
export const RESENDABLE_STATUSES: readonly ReservationStatus[] = [
  'awaiting_check',
  'check_received',
  'check_cleared',
  'pending_approval',
  'confirmed',
];

export function canResendLetter(status: ReservationStatus): boolean {
  return RESENDABLE_STATUSES.includes(status);
}

/**
 * The key the door roster sorts a vendor under.
 *
 * The club collects one name field, so a surname has to be guessed, and the
 * guess is the last whitespace-separated word. That is right for "Bob Smith"
 * and wrong for "Bob Van Dyke", which files under Dyke. It is nonetheless the
 * behaviour the roster has always had, and the officer-facing edit form shows
 * the result of this function as you type — so the person correcting a name
 * can see where it will land on the morning of the meet, and write it in a way
 * that sorts where they expect.
 *
 * Returns "Smith Bob Smith": surname first to sort, full name after to break
 * ties between two people who share one.
 */
export function rosterSortName(name: string): string {
  const full = name.trim().replace(/\s+/g, ' ');
  if (!full) return '';
  const last = rosterSurname(full);
  return `${last} ${full}`.toLowerCase();
}

/** Just the surname the roster will file this name under. For the edit form. */
export function rosterSurname(name: string): string {
  const full = name.trim().replace(/\s+/g, ' ');
  if (!full) return '';
  const parts = full.split(' ');
  return parts[parts.length - 1] ?? full;
}

// ---------------------------------------------------------------------------
// Pricing
// ---------------------------------------------------------------------------

/**
 * The one place a total is computed. Functions use it; the UI only previews.
 *
 * The browser never sends a price — it sends table IDs, and the server prices
 * them. Which rate applies is decided here too, from the payment method and
 * the club's policy, so no caller can pick the cheaper one.
 */
export function totalForTables(
  tables: readonly Pick<Table, 'priceOverride'>[],
  event: Pick<SwapMeetEvent, 'pricePerTable' | 'dayOfPricePerTable'>,
  rate: 'advance' | 'dayOf' = 'advance',
): Cents {
  const base = rate === 'dayOf' ? event.dayOfPricePerTable : event.pricePerTable;
  return tables.reduce((sum, t) => sum + (t.priceOverride ?? base), 0);
}

/** Which rate a reservation is charged, given how the vendor chose to pay. */
export function rateFor(
  method: PaymentMethod,
  policy: Pick<EventPolicy, 'atDoorUsesDayOfRate'>,
): 'advance' | 'dayOf' {
  if (method === 'at_door' && policy.atDoorUsesDayOfRate) return 'dayOf';
  return 'advance';
}

/**
 * The club's timezone.
 *
 * Every date in this system is a date in Lexington, Kentucky: the meet is a
 * Saturday morning there, and the check deadline is the end of 15 November
 * there. The stored millisecond value for that deadline is 2026-11-16T04:59Z,
 * so where it is rendered decides what day it reads as — and the two places
 * that render it do not agree on a timezone by default. A browser uses the
 * vendor's; Cloud Functions run with TZ=UTC.
 *
 * Left to the default, the confirmation email would have told every vendor the
 * deadline was 16 November, a day after the club's actual cutoff — inviting
 * exactly the late checks the deadline exists to prevent.
 */
export const CLUB_TIME_ZONE = 'America/New_York';

/** "November 15". A club date, rendered the same wherever this runs. */
export function clubDate(
  ms: Millis,
  style: 'long' | 'short' = 'long',
): string {
  return new Date(ms).toLocaleDateString('en-US', {
    month: style,
    day: 'numeric',
    timeZone: CLUB_TIME_ZONE,
  });
}

/**
 * A date with the time on it, for the console rather than for a letter.
 *
 * `clubDate` gives a month and a day, which is what a vendor's confirmation
 * wants — nobody needs the minute their tables were held. An officer looking
 * at the record sometimes does: several bookings land on the same afternoon,
 * and "which of these two came first" is a real question when a table is
 * disputed.
 *
 * Rendered in the club's own time zone like every other date here, so a
 * booking taken at eleven at night does not appear to have happened the
 * following day. No year: the deployment serves one meet at a time, so every
 * reservation on screen is from the same season and the year would be noise.
 */
export function clubDateTime(ms: Millis): string {
  return new Date(ms).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: CLUB_TIME_ZONE,
  });
}

export function formatCents(cents: Cents): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

// ---------------------------------------------------------------------------
// Firestore paths — one definition, used by both sides
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Confirmation codes
// ---------------------------------------------------------------------------

/**
 * Alphabet for confirmation codes.
 *
 * Deliberately excludes characters that get confused when handwritten on a
 * check or read aloud: O and 0, I and 1 and L, S and 5, B and 8. What is left
 * survives a treasurer squinting at someone's handwriting.
 */
const CODE_ALPHABET = '234679ACDEFGHJKMNPQRTUVWXYZ';
const CODE_LENGTH = 4;

/** "LMAC-7K4Q". The prefix makes it obvious what the code belongs to. */
export function formatCode(body: string): string {
  return `LMAC-${body}`;
}

/**
 * A candidate code. Not guaranteed unique — the caller writes it inside a
 * transaction that retries on collision, which is the only place uniqueness
 * can actually be enforced.
 */
export function generateCodeBody(random: () => number = Math.random): string {
  let out = '';
  for (let i = 0; i < CODE_LENGTH; i += 1) {
    out += CODE_ALPHABET[Math.floor(random() * CODE_ALPHABET.length)];
  }
  return out;
}

/**
 * Accepts what a person typed and returns the canonical code, or null.
 *
 * Forgiving on purpose: a treasurer typing a code off a handwritten check
 * should not have to care about case, spaces, dashes, or the LMAC prefix.
 *
 * The substitutions map each excluded character to the one it is most often
 * mistaken for, in the same direction every time. Since no generated code can
 * contain O, 0, I, L, 1, S, 5, B or 8, reading such a character means the
 * writer's hand — or the reader's eye — turned a legal character into an
 * illegal one, and mapping it back recovers the original.
 */
export function parseCode(input: string): string | null {
  const cleaned = input
    .toUpperCase()
    .replace(/^LMAC/, '')
    .replace(/[^A-Z0-9]/g, '')
    .replace(/[O]/g, 'Q')
    .replace(/[0]/g, 'Q')
    .replace(/[IL1]/g, 'J')
    .replace(/[S5]/g, '6')
    .replace(/[B8]/g, '9');
  if (cleaned.length !== CODE_LENGTH) return null;
  if (![...cleaned].every((c) => CODE_ALPHABET.includes(c))) return null;
  return formatCode(cleaned);
}

/**
 * Who the club wants told when something happens.
 *
 * Club-wide rather than per-event: the same officers want notifying whichever
 * swap meet it is, and duplicating the list per event is how one copy ends up
 * stale.
 */
export interface NotificationSettings {
  /** Every address here is copied on vendor-facing mail. */
  recipients: string[];
  /** Appears as the sender, and as the reply-to on vendor mail. */
  fromName: string;
  fromAddress: string;
  /** Switch off without losing the list — useful while testing. */
  enabled: boolean;
  updatedAt: Millis;
  updatedBy: string | null;
}

export const DEFAULT_NOTIFICATION_SETTINGS: Omit<
  NotificationSettings,
  'updatedAt' | 'updatedBy'
> = {
  recipients: ['LMACswapmeet@gmail.com'],
  fromName: 'Lexington Model Airplane Club',
  fromAddress: 'LMACswapmeet@gmail.com',
  enabled: true,
};

export const paths = {
  admins: () => 'admins',
  admin: (uid: string) => `admins/${uid}`,
  /** Invited officers, keyed by lowercased email address. */
  adminInvites: () => 'adminInvites',
  adminInvite: (email: string) => `adminInvites/${email.trim().toLowerCase()}`,
  events: () => 'events',
  event: (eventId: string) => `events/${eventId}`,
  tables: (eventId: string) => `events/${eventId}/tables`,
  table: (eventId: string, tableId: string) =>
    `events/${eventId}/tables/${tableId}`,
  publicMap: (eventId: string) => `events/${eventId}/publicMap`,
  publicMapChunk: (eventId: string) => `events/${eventId}/publicMap/all`,
  reservations: (eventId: string) => `events/${eventId}/reservations`,
  /** Claimed confirmation codes, so uniqueness can be enforced atomically. */
  reservationCodes: (eventId: string) => `events/${eventId}/reservationCodes`,
  reservationCode: (eventId: string, code: string) =>
    `events/${eventId}/reservationCodes/${code}`,
  reservation: (eventId: string, reservationId: string) =>
    `events/${eventId}/reservations/${reservationId}`,
  /** Club-wide settings that are not tied to one swap meet. */
  notificationSettings: () => 'settings/notifications',
  /** Outgoing mail. One document per message, sent by a trigger. */
  mail: () => 'mail',
  payments: () => 'payments',
  payment: (paymentId: string) => `payments/${paymentId}`,
} as const;

/** One place to normalise an address, so a lookup can never miss on case. */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** Cheap sanity check before writing an invite. Not RFC-complete on purpose. */
export function looksLikeEmail(email: string): boolean {
  const e = normalizeEmail(email);
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e);
}

/**
 * The swap meet this deployment serves.
 *
 * One meet at a time, deliberately: the club runs one a year, and a single id
 * keeps every query, rule and function pointing at the same place. Next year,
 * change it here and nowhere else.
 */
export const CURRENT_EVENT_ID = 'swap-2026-11-21';

export const DEFAULT_EVENT_POLICY: EventPolicy = {
  holdMinutes: 15,
  paypalHoldMinutes: 30,
  checkDeadlineIsReceipt: true,
  tentativeCutoffDaysBeforeEvent: 7,
  allowPayAtDoor: true,
  allowCheckByMail: true,
  atDoorUsesDayOfRate: true,
  refundPolicyText:
    'Table fees are refundable up to 14 days before the swap meet. ' +
    'After that the club cannot refund, as the hall is already committed.',
  checkMailingAddress: '437 Austerlitz Road, Paris, KY 40361',
  checkPayableTo: 'LMAC Treasurer',
};
